import React, { useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCollection, addDocument, updateDocument, deleteDocument, where } from '@/hooks/useFirestore';
import { storage, db } from '@/config/firebase';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { 
  Upload, 
  Search, 
  Image as ImageIcon,
  Video,
  Trash2,
  Grid,
  List,
  Play,
  Clock,
  HardDrive,
  Filter
} from 'lucide-react';

const MediaLibrary = () => {
  const { isHQ } = useAuth();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [previewMedia, setPreviewMedia] = useState(null);

  const { documents: media, loading } = useCollection('media');
  const { documents: screens } = useCollection('screens');
  const { documents: locations } = useCollection('locations');

  const filteredMedia = useMemo(() => {
    return media.filter(item => {
      const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = typeFilter === 'all' || item.type === typeFilter;
      return matchesSearch && matchesType;
    }).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  }, [media, searchTerm, typeFilter]);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!isHQ) {
      toast({ title: 'Permission denied', variant: 'destructive' });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i];
      const fileId = uuidv4();
      const fileExt = file.name.split('.').pop();
      const filePath = `media/${fileId}.${fileExt}`;
      const storageRef = ref(storage, filePath);

      const uploadTask = uploadBytesResumable(storageRef, file);

      await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = ((i + snapshot.bytesTransferred / snapshot.totalBytes) / acceptedFiles.length) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            toast({ title: 'Upload failed', description: error.message, variant: 'destructive' });
            reject(error);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const type = file.type.startsWith('image') ? 'image' : 'video';
            
            await setDoc(doc(db, 'media', fileId), {
              id: fileId,
              name: file.name,
              url: downloadURL,
              type,
              size: file.size,
              mimeType: file.type,
              storagePath: filePath,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
            resolve();
          }
        );
      });
    }

    setUploading(false);
    setUploadProgress(0);
    toast({ title: `${acceptedFiles.length} file(s) uploaded successfully` });
  }, [isHQ, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'video/*': ['.mp4', '.webm', '.mov']
    },
    disabled: !isHQ || uploading
  });

  const handleDeleteMedia = async (mediaItem) => {
    try {
      // Delete from storage
      if (mediaItem.storagePath) {
        const storageRef = ref(storage, mediaItem.storagePath);
        await deleteObject(storageRef).catch(() => {});
      }
      // Delete from Firestore
      await deleteDocument('media', mediaItem.id);
      toast({ title: 'Media deleted successfully' });
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, variant: 'destructive' });
    }
  };

  const handleBulkDelete = async () => {
    for (const mediaId of selectedMedia) {
      const item = media.find(m => m.id === mediaId);
      if (item) await handleDeleteMedia(item);
    }
    setSelectedMedia([]);
  };

  const toggleSelectMedia = (mediaId) => {
    if (selectedMedia.includes(mediaId)) {
      setSelectedMedia(selectedMedia.filter(id => id !== mediaId));
    } else {
      setSelectedMedia([...selectedMedia, mediaId]);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const totalStorage = media.reduce((acc, m) => acc + (m.size || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Media Library</h1>
          <p className="text-slate-400">Upload and manage your media files</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <HardDrive className="w-4 h-4" />
            <span>{formatFileSize(totalStorage)} used</span>
          </div>
        </div>
      </div>

      {/* Upload Zone */}
      {isHQ && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer
            ${isDragActive ? 'border-indigo-500 bg-indigo-500/10' : 'border-slate-700 hover:border-slate-600 bg-slate-800/30'}
            ${uploading ? 'pointer-events-none opacity-50' : ''}`}
        >
          <input {...getInputProps()} />
          <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
          {uploading ? (
            <div className="space-y-2">
              <p className="text-slate-300">Uploading...</p>
              <Progress value={uploadProgress} className="w-64 mx-auto" />
              <p className="text-sm text-slate-500">{Math.round(uploadProgress)}%</p>
            </div>
          ) : isDragActive ? (
            <p className="text-indigo-400">Drop files here...</p>
          ) : (
            <>
              <p className="text-slate-300">Drag & drop files here, or click to select</p>
              <p className="text-sm text-slate-500 mt-2">Supports images (PNG, JPG, GIF, WebP) and videos (MP4, WebM, MOV)</p>
            </>
          )}
        </div>
      )}

      {/* Filters & Actions */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search media..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800/50 border-slate-700"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40 bg-slate-800/50 border-slate-700">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="image">Images</SelectItem>
            <SelectItem value="video">Videos</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex border border-slate-700 rounded-lg overflow-hidden">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('grid')}
            className="rounded-none"
          >
            <Grid className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="rounded-none"
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
        {selectedMedia.length > 0 && isHQ && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedMedia.length})
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-slate-800 border-slate-700">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Selected Media</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete {selectedMedia.length} file(s)? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {/* Media Grid/List */}
      {filteredMedia.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ImageIcon className="w-12 h-12 text-slate-500 mb-4" />
            <p className="text-slate-400">No media files found</p>
            {isHQ && <p className="text-sm text-slate-500">Upload files using the area above</p>}
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {filteredMedia.map(item => (
            <div
              key={item.id}
              className={`relative group rounded-lg overflow-hidden border-2 transition-all cursor-pointer
                ${selectedMedia.includes(item.id) ? 'border-indigo-500' : 'border-transparent hover:border-slate-600'}`}
              onClick={() => setPreviewMedia(item)}
            >
              {isHQ && (
                <div
                  className="absolute top-2 left-2 z-10"
                  onClick={(e) => { e.stopPropagation(); toggleSelectMedia(item.id); }}
                >
                  <Checkbox checked={selectedMedia.includes(item.id)} />
                </div>
              )}
              <div className="aspect-video bg-slate-900">
                {item.type === 'image' ? (
                  <img src={item.url} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center relative">
                    <video src={item.url} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <Play className="w-8 h-8 text-white" />
                    </div>
                  </div>
                )}
              </div>
              <div className="p-2 bg-slate-800">
                <p className="text-xs text-white truncate">{item.name}</p>
                <div className="flex items-center justify-between mt-1">
                  <Badge variant="secondary" className="text-xs px-1">
                    {item.type === 'image' ? <ImageIcon className="w-3 h-3" /> : <Video className="w-3 h-3" />}
                  </Badge>
                  <span className="text-xs text-slate-500">{formatFileSize(item.size)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredMedia.map(item => (
            <div
              key={item.id}
              className={`flex items-center gap-4 p-3 rounded-lg border transition-colors cursor-pointer
                ${selectedMedia.includes(item.id) ? 'border-indigo-500 bg-indigo-500/10' : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'}`}
              onClick={() => setPreviewMedia(item)}
            >
              {isHQ && (
                <div onClick={(e) => { e.stopPropagation(); toggleSelectMedia(item.id); }}>
                  <Checkbox checked={selectedMedia.includes(item.id)} />
                </div>
              )}
              <div className="w-20 h-12 rounded overflow-hidden bg-slate-900 flex-shrink-0">
                {item.type === 'image' ? (
                  <img src={item.url} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Video className="w-6 h-6 text-slate-500" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white truncate">{item.name}</p>
                <p className="text-xs text-slate-500">{item.mimeType}</p>
              </div>
              <Badge variant="secondary">
                {item.type}
              </Badge>
              <span className="text-sm text-slate-400">{formatFileSize(item.size)}</span>
              {isHQ && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()} className="text-red-400 hover:text-red-300">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-slate-800 border-slate-700">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Media</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{item.name}"?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDeleteMedia(item)} className="bg-red-600 hover:bg-red-700">
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewMedia} onOpenChange={() => setPreviewMedia(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-4xl">
          <DialogHeader>
            <DialogTitle>{previewMedia?.name}</DialogTitle>
            <DialogDescription>
              {previewMedia?.type} • {formatFileSize(previewMedia?.size || 0)}
            </DialogDescription>
          </DialogHeader>
          <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden">
            {previewMedia?.type === 'image' ? (
              <img src={previewMedia?.url} alt={previewMedia?.name} className="w-full h-full object-contain" />
            ) : (
              <video src={previewMedia?.url} controls className="w-full h-full" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediaLibrary;
